import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTheme, useMediaQuery } from '@mui/material';

const SunContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 5%;
  transform: translateY(-50%);
  width: 300px;
  height: 300px;
  z-index: 1;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;

  &.visible {
    opacity: 1;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const SunSVG = styled.svg`
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 0 20px rgba(255, 165, 0, 0.5));
`;

const AnimatedSun: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (isMobile) return; // Don't add scroll listener on mobile

    const handleScroll = () => {
      if (!containerRef.current) return;

      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const triggerPosition = windowHeight * 0.3;

      if (scrollPosition > triggerPosition) {
        containerRef.current.classList.add('visible');
      } else {
        containerRef.current.classList.remove('visible');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  if (isMobile) return null; // Don't render the component on mobile

  return (
    <SunContainer ref={containerRef}>
      <SunSVG viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        {/* Outer rays */}
        <g className="rays">
          {[...Array(12)].map((_, i) => (
            <path
              key={i}
              d={`M50,50 L50,${i % 2 === 0 ? '10' : '15'}`}
              stroke="orange"
              strokeWidth="2"
              fill="none"
              transform={`rotate(${i * 30} 50 50)`}
            />
          ))}
        </g>
        
        {/* Main sun circle */}
        <circle
          cx="50"
          cy="50"
          r="25"
          fill="url(#sunGradient)"
        />

        {/* Gradient definition */}
        <defs>
          <radialGradient id="sunGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
            <stop offset="0%" stopColor="#FFD700" />
            <stop offset="100%" stopColor="#FFA500" />
          </radialGradient>
        </defs>

        {/* Animation */}
        <style>
          {`
            .rays path {
              animation: pulse 2s ease-in-out infinite;
              animation-delay: calc(var(--i) * 0.1s);
            }
            @keyframes pulse {
              0%, 100% { opacity: 0.5; }
              50% { opacity: 1; }
            }
          `}
        </style>
      </SunSVG>
    </SunContainer>
  );
};

export default AnimatedSun; 